import {
    Button,
    Flex,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
    Text,
    Textarea,
    TextInput,
    Title,
} from '@tremor/react'
import { useEffect, useState } from 'react'
import { ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useLocation } from 'react-router-dom'
import {
    useApiV1KeypairDelete,
    useApiV1KeypairImportCreate,
    useApiV1KeypairsList,
} from '../../api/api.gen'
import Modal from '../../components/Layout/modal'
import { toErrorString } from '../../utilities/error'
import Confirm from '../../components/Layout/confirm'

export default function SSHKeyPage() {
    const location = useLocation()
    const [deleteID, setDeleteID] = useState<number>()
    const [showModal, setShowModal] = useState(false)
    const [keyName, setKeyName] = useState('')
    const [publicKey, setPublicKey] = useState('')
    const {
        isLoading: keypairLoading,
        isExecuted: keypairExecuted,
        response: keypairResponse,
        error: keypairError,
        sendNowWithParams: importKeyPair,
    } = useApiV1KeypairImportCreate({}, {}, false)

    const {
        isLoading: deleteLoading,
        isExecuted: deleteExecuted,
        sendNowWithParams: deleteKey,
        error: deleteError,
    } = useApiV1KeypairDelete({ id: 0 }, {}, false)
    const { response: keypairList, sendNow: refresh } = useApiV1KeypairsList()

    useEffect(() => {
        if (
            keypairExecuted &&
            !keypairLoading &&
            toErrorString(keypairError).length === 0
        ) {
            setShowModal(false)
            refresh()
        }
    }, [keypairLoading])

    useEffect(() => {
        if (
            deleteExecuted &&
            !deleteLoading &&
            toErrorString(deleteError).length === 0
        ) {
            setDeleteID(undefined)
            refresh()
        }
    }, [deleteLoading])

    useEffect(() => {
        if (location.hash === '#import') {
            setShowModal(true)
        }
    }, [])

    return (
        <Flex
            className="p-4 bg-gray-50 w-auto min-w-[1000px] max-w-fit mt-4 rounded-md"
            flexDirection="col"
            justifyContent="start"
            alignItems="start"
        >
            <Flex flexDirection="row" justifyContent="between">
                <Title className="text-teal-600">SSH Keys</Title>
                <Flex className="w-fit space-x-2">
                    <Button
                        icon={ArrowPathIcon}
                        variant="secondary"
                        color="teal"
                        disabled={keypairExecuted && keypairLoading}
                        onClick={() => refresh()}
                    />
                    <Button
                        variant="secondary"
                        color="teal"
                        onClick={() => setShowModal(true)}
                    >
                        Import
                    </Button>
                </Flex>
            </Flex>
            <Table className="w-full">
                <TableHead>
                    <TableRow>
                        <TableHeaderCell>Name</TableHeaderCell>
                        <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {keypairList?.items?.map((v) => (
                        <TableRow>
                            <TableCell>{v.name}</TableCell>
                            <TableCell>
                                <TrashIcon
                                    onClick={() => setDeleteID(v.id)}
                                    className="w-4 h-4 text-teal-500 cursor-pointer"
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                    {(keypairList?.items?.length || 0) === 0 && (
                        <TableRow>
                            <TableCell colSpan={6}>
                                <Text className="w-full text-gray-400 text-center font-normal">
                                    {keypairExecuted && keypairLoading
                                        ? 'Loading'
                                        : 'Empty list'}
                                </Text>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <Confirm
                title={`Are you sure you want to delete ${keypairList?.items
                    ?.filter((v) => v.id === deleteID)
                    .map((v) => v.name)}?`}
                show={deleteID !== undefined}
                loading={deleteExecuted && deleteLoading}
                error={deleteError}
                onCanceled={() => setDeleteID(undefined)}
                onConfirmed={() => deleteKey({ id: deleteID || 0 }, {})}
            />

            <Modal
                title="Import a new SSH Key"
                show={showModal}
                onCloseClicked={() => setShowModal(false)}
            >
                <Text className="mt-4 mb-1">Name:</Text>
                <TextInput
                    value={keyName}
                    onValueChange={setKeyName}
                    error={keyName.length === 0}
                    errorMessage="Name is required"
                />

                <Text className="mt-4 mb-1">Public Key:</Text>
                <Textarea
                    className="h-60"
                    value={publicKey}
                    onValueChange={setPublicKey}
                    error={publicKey.length === 0}
                    errorMessage="Public key is required"
                />

                <Button
                    className="mt-8"
                    color="teal"
                    loading={keypairLoading && keypairExecuted}
                    disabled={keypairLoading && keypairExecuted}
                    onClick={() => {
                        importKeyPair(
                            {
                                name: keyName,
                                public: publicKey,
                            },
                            {}
                        )
                    }}
                >
                    Import
                </Button>
            </Modal>
        </Flex>
    )
}
